import AccountBalanceWalletRounded from "@mui/icons-material/AccountBalanceWalletRounded";
import AccountTreeRounded from "@mui/icons-material/AccountTreeRounded";
import AddTaskRounded from "@mui/icons-material/AddTaskRounded";
import AllInclusiveRounded from "@mui/icons-material/AllInclusiveRounded";
import AltRouteRounded from "@mui/icons-material/AltRouteRounded";
import DashboardRounded from "@mui/icons-material/DashboardRounded";
import DnsRounded from "@mui/icons-material/DnsRounded";
import HubRounded from "@mui/icons-material/HubRounded";
import MemoryRounded from "@mui/icons-material/MemoryRounded";
import InsightsRounded from "@mui/icons-material/InsightsRounded";
import LocalShippingRounded from "@mui/icons-material/LocalShippingRounded";
import MailRounded from "@mui/icons-material/MailRounded";
import PeopleAltRounded from "@mui/icons-material/PeopleAltRounded";
import PlayCircleOutlineRounded from "@mui/icons-material/PlayCircleOutlineRounded";
import PublicRounded from "@mui/icons-material/PublicRounded";
import ReceiptLongRounded from "@mui/icons-material/ReceiptLongRounded";
import SettingsEthernetRounded from "@mui/icons-material/SettingsEthernetRounded";
import SettingsRounded from "@mui/icons-material/SettingsRounded";
import SmsRounded from "@mui/icons-material/SmsRounded";
import TerminalRounded from "@mui/icons-material/TerminalRounded";
import TravelExploreRounded from "@mui/icons-material/TravelExploreRounded";
import ViewListRounded from "@mui/icons-material/ViewListRounded";
import type { ReactNode } from "react";

export interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  /**
   * 子菜单。有子项时 `to` 不是一个页面，而是这一支的路径前缀（判高亮与自动展开用）。
   *
   * 只有「按类别/种类分支」的两处用它：账号按类别（G / C …）、网关按种类（SUB / CLE …）。
   * 资源区**故意不分支** —— 线路与邮箱是跨类别共用的池子，按类别切会骗人。
   */
  children?: NavItem[];
  /**
   * 已登记但还没开放：显示出来、置灰、点不进去，而不是悄悄不存在。
   * 这种项的 `to` 只是菜单里的身份（去重与排序用），**不是一个能去的地址**。
   *
   * 2026-10-30 之后暂时没有这样的项（C 与 CLE 都开了）。机制留着：
   * `pipelines.CATEGORIES` 里还登记着 Gr 与 Se，它们进菜单时就是这么显示。
   */
  disabled?: boolean;
  /** 置灰的原因，鼠标悬停时说清楚。 */
  note?: string;
}

export interface NavGroup {
  title?: string;
  items: NavItem[];
}

/**
 * 导航的唯一来源：侧边栏、移动端抽屉、面包屑都从这里取。
 *
 * 分组对应五个模块：
 * - **资源** 是任务消耗的一切供给，服务器也是供给，所以排在最前；
 * - **账号** 是产出，按类别分支（`pipelines.CATEGORIES`），成功率是类别内的一个视图；
 * - **网关** 是客户流量的出口，按种类分支（`gateways.KINDS`）—— 各家网关的挂载表、
 *   健康度词汇都不一样，塞进同一张表列头就得取并集；
 * - **任务** 是真正的工作：常驻任务（一直挂着自己盯水位的）、任务批次（一次发起的一批）、
 *   单步作业（每一步的执行）、能做的事（可发起的任务）、日志。
 *   常驻任务排在最前：它是唯一"自己会动"的那一类，人每天先看它；
 * - **成本** 收尾。
 *
 * 类别/种类写死在这里而不是从中台拉：菜单结构要在首屏之前就确定（否则侧栏会跳），
 * 而「有哪些类别」是半年一变的事实。真要动的时候这里和 `CATEGORIES` 一起改。
 */
export const NAV: NavGroup[] = [
  { items: [{ to: "/", label: "总览", icon: <DashboardRounded /> }] },
  {
    title: "资源",
    items: [
      { to: "/resources/servers", label: "服务器", icon: <DnsRounded /> },
      { to: "/resources/agents", label: "驻机程序", icon: <MemoryRounded /> },
      { to: "/resources/mailboxes", label: "邮箱", icon: <MailRounded /> },
      { to: "/resources/sms", label: "接码", icon: <SmsRounded /> },
      { to: "/resources/proxies/dynamic", label: "动态代理", icon: <PublicRounded /> },
      { to: "/resources/proxies/static", label: "静态代理", icon: <SettingsEthernetRounded /> },
      { to: "/resources/search", label: "搜索", icon: <TravelExploreRounded /> },
      { to: "/resources/sandbox", label: "沙箱", icon: <TerminalRounded /> },
    ],
  },
  {
    title: "账号",
    items: [
      {
        to: "/accounts",
        label: "G",
        icon: <PeopleAltRounded />,
        children: [
          { to: "/accounts", label: "账号", icon: <PeopleAltRounded /> },
          { to: "/accounts/success-rate", label: "成功率", icon: <InsightsRounded /> },
        ],
      },
      { to: "/accounts/c", label: "C", icon: <PeopleAltRounded /> },
    ],
  },
  {
    title: "网关",
    items: [
      {
        to: "/gateways/sub",
        label: "SUB",
        icon: <AltRouteRounded />,
        children: [
          { to: "/gateways/sub/placements", label: "挂载", icon: <AltRouteRounded /> },
          { to: "/gateways/sub/deliveries", label: "投递记录", icon: <LocalShippingRounded /> },
        ],
      },
      {
        to: "/gateways/cle",
        label: "CLE",
        icon: <HubRounded />,
        children: [
          { to: "/gateways/cle/placements", label: "挂载", icon: <HubRounded /> },
          { to: "/gateways/cle/operations", label: "操作记录", icon: <LocalShippingRounded /> },
        ],
      },
    ],
  },
  {
    title: "任务",
    items: [
      { to: "/tasks/standing", label: "常驻任务", icon: <AllInclusiveRounded /> },
      { to: "/tasks/new", label: "新建任务", icon: <AddTaskRounded /> },
      { to: "/tasks/pipelines", label: "任务批次", icon: <AccountTreeRounded /> },
      { to: "/tasks/runs", label: "单步作业", icon: <PlayCircleOutlineRounded /> },
      { to: "/tasks/types", label: "能做的事", icon: <ViewListRounded /> },
      { to: "/tasks/logs", label: "日志", icon: <ReceiptLongRounded /> },
    ],
  },
  {
    title: "财务与配置",
    items: [
      { to: "/costs", label: "成本", icon: <AccountBalanceWalletRounded /> },
      { to: "/settings", label: "设置", icon: <SettingsRounded /> },
    ],
  },
];

/** 叶子页面（能点进去的那些）连同它所在的分组与父项，面包屑按它找。 */
const FLAT = NAV.flatMap((g) =>
  g.items.flatMap((item) =>
    item.children
      ? item.children.map((child) => ({ ...child, group: g.title, parent: item }))
      : [{ ...item, group: g.title, parent: undefined as NavItem | undefined }],
  ),
);

export function isActive(item: NavItem, pathname: string): boolean {
  if (item.disabled) return false;
  // 有子项的父项：任一子项命中就算这一支在用
  if (item.children) return item.children.some((child) => isActive(child, pathname));
  if (item.to === "/") return pathname === "/";
  // 「账号」不能被 /accounts/success-rate 点亮，否则两项同时高亮
  if (item.to === "/accounts") return pathname === "/accounts";
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

/** 面包屑：分组 › 父项 › 页面 › 详情。详情段由页面通过 useCrumb 补充。 */
export function crumbsFor(pathname: string): { label: string; to?: string }[] {
  const hit = FLAT.filter((i) => isActive(i, pathname)).sort((a, b) => b.to.length - a.to.length)[0];
  if (!hit) return [{ label: "Foxconn Center" }];
  const out: { label: string; to?: string }[] = [];
  if (hit.group) out.push({ label: hit.group });
  // 父项是一支的名字（类别 G、种类 SUB），不是一个页面，所以不带链接
  if (hit.parent) out.push({ label: hit.parent.label });
  out.push({ label: hit.label, to: pathname === hit.to ? undefined : hit.to });
  return out;
}
